import { useCallback } from 'react';

import { checkFeatureFlagGate } from '@wac-web/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@wac-web/utils/feature-flags/feature-flag-manifest';
import { useLocation, useParams } from 'react-router-dom';

import { FASTSHIFT_PATHS, FASTSHIFT_PATH_KEYS, FASTSHIFT_EAP_HOME_PATH } from '../routes/constants';

const PATHS_KEYS = {
	HOME: 'home',
	SELECTION: 'selection',
	PARTNER_RESOURCES: 'partnerResources',
	CS_PROFILER: 'csProfiler',
	DOMAIN_SCOPED_OVERVIEW: 'domainScopedOverview',
	DOMAIN_SCOPED_ORGANIZATIONS: 'domainScopedOrganizations',
	DOMAIN_SCOPED_ORGANIZATION_DETAILS: 'domainScopedOrganizationDetails',
	DOMAIN_SCOPED_DC_INSTANCE_DETAILS: 'domainScopedDcInstanceDetails',
	DOMAIN_SCOPED_PLANNING: 'domainScopedPlanning',
	DOMAIN_SCOPED_TICKETS: 'domainScopedTickets',
	DOMAIN_SCOPED_RESOURCES: 'domainScopedResources',
	EAP_HOME: 'eapHome',
} as const;

type PathKey = (typeof PATHS_KEYS)[keyof typeof PATHS_KEYS];
type PartnerScopedPathKey =
	| typeof PATHS_KEYS.HOME
	| typeof PATHS_KEYS.SELECTION
	| typeof PATHS_KEYS.PARTNER_RESOURCES
	| typeof PATHS_KEYS.EAP_HOME
	| typeof PATHS_KEYS.CS_PROFILER;
type DomainScopedPathKey = Exclude<PathKey, PartnerScopedPathKey>;

type FastshiftPaths = Record<PartnerScopedPathKey, string> &
	Partial<Record<DomainScopedPathKey, string>>;

type UseRoutes = () => {
	domainId: string | undefined;
	fullPathname: string;
	paths: FastshiftPaths;
	getPathByDomainId: (path: string, targetDomainId: string) => string;
	getPathByOrganizationId: (path: string, targetOrganizationId: string) => string;
	getPathByDcInstanceId: (path: string, targetInstanceId: string) => string;
	isValidFastshiftRoute: (pathname: string) => boolean;
	isValidFastshiftPartnerRoute: (pathname: string) => boolean;
};

export const useRoutes: UseRoutes = () => {
	const { pathname } = useLocation();
	const isCsProfilerRouteEnabled = checkFeatureFlagGate(featureFlags.sxpFspToolsMenuCsProfiler);
	const { locale, domainId, organizationId, instanceId } = useParams<{
		locale: string;
		domainId: string;
		organizationId: string;
		instanceId: string;
	}>();

	const injectParams = useCallback(
		(
			path: string,
			params: {
				domainId?: string;
				organizationId?: string;
				instanceId?: string;
			} = {},
		) => {
			return `${locale ? `/${locale}/` : '/'}${path
				.replace(':domainId', params.domainId ?? '')
				.replace(':organizationId', params.organizationId ?? '')
				.replace(':instanceId', params.instanceId ?? '')}`;
		},
		[locale],
	);

	const paths: FastshiftPaths = {
		[PATHS_KEYS.HOME]: injectParams(FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.FASTSHIFT]),
		[PATHS_KEYS.SELECTION]: injectParams(FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.SELECTION]),
		[PATHS_KEYS.PARTNER_RESOURCES]: injectParams(
			FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.RESOURCES_PARTNER_SCOPED],
		),
		[PATHS_KEYS.CS_PROFILER]: injectParams(FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.CS_PROFILER]),
		...(domainId
			? {
					[PATHS_KEYS.DOMAIN_SCOPED_OVERVIEW]: injectParams(
						FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.OVERVIEW],
						{ domainId },
					),
					[PATHS_KEYS.DOMAIN_SCOPED_ORGANIZATIONS]: injectParams(
						FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.ORGANIZATIONS],
						{ domainId },
					),
					[PATHS_KEYS.DOMAIN_SCOPED_PLANNING]: injectParams(
						FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.PLANNING],
						{ domainId },
					),
					[PATHS_KEYS.DOMAIN_SCOPED_TICKETS]: injectParams(
						FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.TICKETS],
						{ domainId },
					),
					[PATHS_KEYS.DOMAIN_SCOPED_RESOURCES]: injectParams(
						FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.RESOURCES_DOMAIN_SCOPED],
						{ domainId },
					),
				}
			: {}),
		...(domainId && organizationId
			? {
					[PATHS_KEYS.DOMAIN_SCOPED_ORGANIZATION_DETAILS]: injectParams(
						FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.ORGANIZATION_DETAILS],
						{ domainId, organizationId },
					),
				}
			: {}),
		...(domainId && organizationId && instanceId
			? {
					[PATHS_KEYS.DOMAIN_SCOPED_DC_INSTANCE_DETAILS]: injectParams(
						FASTSHIFT_PATHS[FASTSHIFT_PATH_KEYS.DC_INSTANCE_DETAILS],
						{ domainId, organizationId, instanceId },
					),
				}
			: {}),
		[PATHS_KEYS.EAP_HOME]: injectParams(FASTSHIFT_EAP_HOME_PATH),
	};

	const getPathByDomainId = (path: string, targetDomainId: string) => {
		return injectParams(path, {
			domainId: targetDomainId,
			organizationId,
			instanceId,
		});
	};

	const getPathByOrganizationId = (path: string, targetOrganizationId: string) => {
		return injectParams(path, {
			domainId,
			organizationId: targetOrganizationId,
			instanceId,
		});
	};

	const getPathByDcInstanceId = (path: string, targetInstanceId: string) => {
		return injectParams(path, {
			domainId,
			organizationId,
			instanceId: targetInstanceId,
		});
	};

	const isValidFastshiftRoute = (pathname: string) => {
		if (pathname === paths.csProfiler && !isCsProfilerRouteEnabled) {
			return false;
		}

		return Object.entries(paths).some(
			([pathKey, path]) => pathKey !== PATHS_KEYS.EAP_HOME && path === pathname,
		);
	};

	const isValidFastshiftPartnerRoute = (pathname: string) => {
		return [paths.selection, paths.partnerResources].includes(pathname);
	};

	return {
		domainId,
		fullPathname: pathname,
		paths,
		getPathByDomainId,
		getPathByOrganizationId,
		getPathByDcInstanceId,
		isValidFastshiftRoute,
		isValidFastshiftPartnerRoute,
	};
};
